/**
 * Encrypted cache utilities using the Web Crypto API.
 *
 * Strategy:
 *  - PBKDF2 to derive a strong AES-GCM key from the user ID (+ salt)
 *  - AES-GCM 256-bit for authenticated encryption
 *  - All binary data encoded as base64url strings for IndexedDB storage
 */

// ─── Encoding helpers ─────────────────────────────────────────────────────────

// Em fatias: `String.fromCharCode(...bytes)` com o buffer inteiro estoura o
// teto de argumentos da engine (RangeError) em payload de MB — o snapshot de
// um canal grande — e o cache nunca era gravado.
const B64_CHUNK = 0x8000;

function bufToB64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i += B64_CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + B64_CHUNK));
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function b64ToBuf(b64: string): ArrayBuffer {
    const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(padded);
    const buf = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
    return buf.buffer;
}

// ─── Key derivation ───────────────────────────────────────────────────────────

const KEY_CACHE_MAX = 50;
const keyCache = new Map<string, CryptoKey>();

/**
 * Derives an AES-GCM CryptoKey from a user identifier.
 * The salt ensures that different sessions (or users) produce different keys.
 * Results are cached in memory to avoid repeated PBKDF2 derivation.
 */
export async function deriveKey(userId: string, salt: ArrayBuffer): Promise<CryptoKey> {
    const cacheId = userId + ':' + bufToB64(salt);
    const cached = keyCache.get(cacheId);
    if (cached) return cached;

    const encoder = new TextEncoder();
    const rawKey = await crypto.subtle.importKey(
        'raw',
        encoder.encode(userId),
        'PBKDF2',
        false,
        ['deriveKey'],
    );
    const key = await crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt,
            iterations: 10_000,
            hash: 'SHA-256',
        },
        rawKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    );

    // Evict oldest entries to prevent unbounded growth
    if (keyCache.size >= KEY_CACHE_MAX) {
        const first = keyCache.keys().next().value;
        if (first !== undefined) keyCache.delete(first);
    }
    keyCache.set(cacheId, key);
    return key;
}

// ─── Encrypt / Decrypt ────────────────────────────────────────────────────────

export interface EncryptedPayload {
    iv: string;
    salt: string;
    data: string;
}

/**
 * Encrypts any JSON-serializable value with AES-GCM.
 * Returns IV + salt + ciphertext, all as base64url strings.
 * When `reuseSaltB64` is provided, the same salt is used so the derived key
 * can be served from the in-memory cache (avoids PBKDF2 re-derivation).
 */
export async function encrypt<T>(userId: string, value: T, reuseSaltB64?: string): Promise<EncryptedPayload> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const salt = reuseSaltB64
        ? new Uint8Array(b64ToBuf(reuseSaltB64))
        : crypto.getRandomValues(new Uint8Array(16));
    const key = await deriveKey(userId, salt.buffer);
    const plaintext = new TextEncoder().encode(JSON.stringify(value));
    const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        plaintext,
    );
    return {
        iv: bufToB64(iv.buffer),
        salt: bufToB64(salt.buffer),
        data: bufToB64(ciphertext),
    };
}

/**
 * Decrypts a payload created by `encrypt`.
 * Returns the original value, or null if decryption fails (e.g. wrong user).
 */
export async function decrypt<T>(userId: string, payload: EncryptedPayload): Promise<T | null> {
    try {
        const iv = b64ToBuf(payload.iv);
        const salt = b64ToBuf(payload.salt);
        const ciphertext = b64ToBuf(payload.data);
        const key = await deriveKey(userId, salt);
        const plaintext = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: new Uint8Array(iv) },
            key,
            ciphertext,
        );
        return JSON.parse(new TextDecoder().decode(plaintext)) as T;
    } catch {
        return null;
    }
}
